'use client';
import React, {useEffect, useState} from 'react';
import cn from 'classnames';
import {CalculateTimeLeftResult, calculateTimeLeft} from '@/_application/core/utils/date-time';

interface IProps {
    dateTo: string;
    onTimeUp?: () => void;
    className?: string;
    elementClassName?: string;
    isDisplayDay?: boolean;
    isDisplayLabel?: boolean;
    labelClassName?: string;
    dividerClassName?: string;
    divider?: string;
    timeClassName?: string;
}

const classes = {
    timeRoot: 'text-center',
};

/**
 * Timer component with countdown functionality.
 * Uses client-only rendering for time values to avoid hydration mismatch
 * since countdown values differ between server and client render time.
 */
function Timer({
    dateTo,
    onTimeUp,
    className,
    elementClassName,
    isDisplayDay,
    isDisplayLabel,
    labelClassName,
    divider,
    dividerClassName,
    timeClassName,
}: Readonly<IProps>) {
    // Track if component has mounted to avoid hydration mismatch
    const [isMounted, setIsMounted] = useState(false);
    const [timeLeft, setTimeLeft] = useState<CalculateTimeLeftResult>(() => calculateTimeLeft(dateTo, isDisplayDay));

    const timeClass = cn(classes.timeRoot, timeClassName);

    // Set mounted state after hydration
    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isMounted) return;

        if (timeLeft.isTimeUp && timeLeft.isValidDate && onTimeUp) {
            onTimeUp();
            return;
        }
        const timer = setTimeout(() => {
            setTimeLeft(calculateTimeLeft(dateTo, isDisplayDay));
        }, 1000);
        return () => clearTimeout(timer);
    }, [timeLeft, dateTo, isDisplayDay, onTimeUp, isMounted]);

    // Show placeholder during SSR and initial hydration to avoid mismatch
    const displayValue = (value: string | undefined) => {
        if (!isMounted) return '--';
        return timeLeft.isValidDate ? value : '--';
    };

    return (
        <div className={cn('flex', className)}>
            {isDisplayDay && (
                <>
                    <div className="flex flex-col items-center justify-center">
                        <span className={cn(elementClassName)}>
                            <span className={timeClass} suppressHydrationWarning>
                                {displayValue(timeLeft.days)}
                            </span>
                            {isDisplayLabel && <span className={labelClassName}>Ngày</span>}
                        </span>
                    </div>
                    {divider && <span className={dividerClassName}>{divider}</span>}
                </>
            )}

            <div className="flex flex-col items-center justify-center">
                <span className={cn(elementClassName)}>
                    <span className={timeClass} suppressHydrationWarning>
                        {displayValue(timeLeft.hours)}
                    </span>
                    {isDisplayLabel && <span className={labelClassName}>Giờ</span>}
                </span>
            </div>

            {divider && <span className={dividerClassName}>{divider}</span>}

            <div className="flex flex-col items-center justify-center">
                <span className={cn(elementClassName)}>
                    <span className={timeClass} suppressHydrationWarning>
                        {displayValue(timeLeft.minutes)}
                    </span>
                    {isDisplayLabel && <span className={labelClassName}>Phút</span>}
                </span>
            </div>

            {divider && <span className={dividerClassName}>{divider}</span>}

            <div className="flex flex-col items-center justify-center">
                <span className={cn(elementClassName)}>
                    <span className={timeClass} suppressHydrationWarning>
                        {displayValue(timeLeft.seconds)}
                    </span>
                    {isDisplayLabel && <span className={labelClassName}>Giây</span>}
                </span>
            </div>
        </div>
    );
}

export default Timer;
